/**
 * v0.4後半 月次実績一括入力 第2フェーズ I-6 出力機能 (§8-5)。
 * 項目カタログの「分類」は画面専用の表示グルーピング (バックエンドのカタログ
 * `apps/api/src/lib/attendance-export/catalog.ts` は sensitivity/value_kind のみ持ち分類を持たない)。
 *
 * ⚠️ 名称が近い項目の見分け (design-expert 6巡目 Major、D-1046): 「予定出勤日数(契約の月平均)」
 * (contracted_monthly_workdays) と「当月の所定出勤日数(シフト上)」(scheduled_workdays_from_schedule)
 * は分類を分ける (前者は既存の「所定」、後者は新設の「当月の実績由来」)。同じ混同が起きうる
 * 月平均所定労働時間/当月の所定労働時間(シフト上)のペアも同様に分ける。
 *
 * 設計: docs/design/v04-monthly-attendance-bulk-entry-phase2-detail.md §8-5
 */

export const ATTENDANCE_EXPORT_CATEGORY_ORDER = [
  '識別', '所定', '当月の実績由来', '属性', '勤怠実績', '労働時間', '遅刻・早退', '確認・承認', '家族', '目標達成率', 'その他',
] as const;
export type AttendanceExportCategory = (typeof ATTENDANCE_EXPORT_CATEGORY_ORDER)[number];

const ITEM_CATEGORY: Record<string, AttendanceExportCategory> = {
  // 人×期間 (person_period)
  employee_number: '識別', employee_name: '識別',
  contracted_monthly_minutes: '所定', contracted_monthly_workdays: '所定',
  scheduled_workdays_from_schedule: '当月の実績由来', scheduled_minutes_from_schedule: '当月の実績由来',
  employment_type: '属性', grade: '属性', position_title: '属性', home_store: '属性', contract_type: '属性',
  hire_date: '属性', termination_date: '属性', in_probation: '属性',
  worked_days: '勤怠実績', scheduled_off_days: '勤怠実績', paid_leave_days: '勤怠実績', comp_leave_days: '勤怠実績',
  absence_days: '勤怠実績', worked_on_off_days: '勤怠実績', proxy_entry_days: '勤怠実績', as_scheduled_days: '勤怠実績',
  off_home_store_work_days: '勤怠実績', minor_days: '勤怠実績', insufficient_break_days: '勤怠実績', approval_status: '勤怠実績',
  total_work_minutes: '労働時間', scheduled_work_minutes: '労働時間', non_statutory_overtime_minutes: '労働時間',
  statutory_overtime_minutes: '労働時間', overtime_over_60h_minutes: '労働時間', late_night_minutes: '労働時間',
  legal_holiday_work_minutes: '労働時間', non_legal_holiday_work_minutes: '労働時間', paid_leave_minutes: '労働時間',
  late_count: '遅刻・早退', late_minutes: '遅刻・早退', early_leave_count: '遅刻・早退', early_leave_minutes: '遅刻・早退',
  spouse_presence: '家族', children_count: '家族',
  achievement_rate: '目標達成率', achievement_status: '目標達成率',
  // 人×日 (person_day)
  employee_number_day: '識別', employee_name_day: '識別',
  date: '属性', weekday: '属性', work_store: '属性',
  absence_kind: '勤怠実績', late_early_reason: '勤怠実績',
  scheduled_pattern: '所定', scheduled_start_end: '所定',
  actual_start_end: '労働時間', break_time: '労働時間', actual_work_minutes_day: '労働時間',
  late_night_minutes_day: '労働時間', statutory_overtime_minutes_day: '労働時間', legal_holiday_work_minutes_day: '労働時間',
  late_early_minutes_day: '遅刻・早退',
  input_source: '確認・承認', as_scheduled_flag: '確認・承認', approver: '確認・承認', approved_at: '確認・承認',
  modified_flag: '確認・承認', deleted_flag: '確認・承認',
};

/** 未知の item_key (将来カタログが増えて対応が漏れた場合) は「その他」に落とす。 */
export const categoryOfItem = (itemKey: string): AttendanceExportCategory => ITEM_CATEGORY[itemKey] ?? 'その他';

/** §8-5 (design-expert 6巡目 Major): 名称が近い項目の見分け用ツールチップ。一言で違いを示す。 */
export const ATTENDANCE_EXPORT_ITEM_TOOLTIPS: Record<string, string> = {
  contracted_monthly_workdays: '契約で決まった月平均の値です。',
  contracted_monthly_minutes: '契約で決まった月平均の値です。',
  scheduled_workdays_from_schedule: 'その月の実際の入力から推定した値です。',
  scheduled_minutes_from_schedule: 'その月の実際の入力から推定した値です。',
};
